import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import useNetworkStatus from '../hooks/useNetworkStatus';
import indexedDBService, {
  getLastSyncTime,
  getPendingChangesCount,
  isOffline,
  isFullSyncNeeded,
  SYNC_DISABLED,
  AUTO_SYNC_ON_REFRESH_DISABLED
} from '../utils/indexedDBService';

// Define sync state interface
interface SyncState {
  isSyncing: boolean;
  lastSyncTime: Date | null;
  pendingChanges: number;
  syncSuccess: boolean;
  syncError: string | null;
  isOfflineMode: boolean;

  syncWithFirebase: (forceSync?: boolean) => Promise<void>;
}

// Create the sync context
const SyncContext = createContext<SyncState | undefined>(undefined);

export const useSyncContext = (): SyncState => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSyncContext must be used within a SyncProvider');
  }
  return context;
};

interface SyncProviderProps {
  children: ReactNode;
}

export const SyncProvider: React.FC<SyncProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { isOnline } = useNetworkStatus();
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [syncSuccess, setSyncSuccess] = useState<boolean>(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<number>(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(getLastSyncTime());
  const [isOfflineMode, setIsOfflineMode] = useState<boolean>(!navigator.onLine);

  // Initialize indexedDB when the app loads
  useEffect(() => {
    const initializeDB = async () => {
      try {
        await indexedDBService.initDB();
      } catch (error) {
        console.error('Error initializing IndexedDB:', error);
        setSyncError('Failed to initialize local database');
      }
    };

    initializeDB();
  }, []);

  // Monitor network status changes
  useEffect(() => {
    const handleOnlineStatusChange = () => {
      setIsOfflineMode(!navigator.onLine);

      // If we just came back online and have pending changes, show a notification
      if (navigator.onLine && pendingChanges > 0) {
        setSyncError(`You're back online with ${pendingChanges} pending changes to sync`);
      }
    };

    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, [pendingChanges]);

  // Check for pending changes and sync info periodically
  useEffect(() => {
    const checkStatus = async () => {
      if (currentUser) {
        try {
          const count = await getPendingChangesCount();
          setPendingChanges(count);
          setLastSyncTime(getLastSyncTime());
        } catch (error) {
          console.error('Error checking status:', error);
        }
      }
    };

    checkStatus();

    // Check for changes every minute
    const interval = setInterval(checkStatus, 60000);

    return () => clearInterval(interval);
  }, [currentUser]);

  // Set up automatic sync every 4 hours - but don't sync immediately on component mount
  useEffect(() => {
    if (!currentUser || !isOnline) return;

    const fourHoursInMs = 4 * 60 * 60 * 1000;

    // Set up interval for auto sync only every 4 hours
    // Note: This scheduled sync should still work even when AUTO_SYNC_ON_REFRESH_DISABLED is true
    const interval = setInterval(() => {
      if (isOnline) {
        // Check if enough time has passed since last sync
        if (isFullSyncNeeded()) {
          syncWithFirebase();
        }
      }
    }, fourHoursInMs);

    return () => clearInterval(interval);
  }, [currentUser, isOnline]);

  // Cache initial data when user logs in for the first time
  useEffect(() => {
    const cacheInitialData = async () => {
      if (currentUser && !isSyncing) {
        try {
          // Check if automatic sync on refresh is disabled
          if (AUTO_SYNC_ON_REFRESH_DISABLED) {
            // Just ensure the user profile is cached without doing a full sync
            await indexedDBService.cacheUserProfile(currentUser.uid);
          } else if (isFullSyncNeeded()) {
            setIsSyncing(true);
            setSyncError(null);

            // Sync all data on first login
            await syncWithFirebase(true);
          } else {
            // Just ensure the user profile is cached
            await indexedDBService.cacheUserProfile(currentUser.uid);
          }
        } catch (error) {
          console.error('Error caching initial data:', error);
          setSyncError('Failed to cache initial data');
        } finally {
          setIsSyncing(false);
        }
      }
    };

    cacheInitialData();
  }, [currentUser]);

  // Sync with Firebase function
  const syncWithFirebase = async (forceSync: boolean = false): Promise<void> => {
    if (!currentUser || isSyncing) return;

    // Check if sync is disabled
    if (SYNC_DISABLED) {
      setSyncError('Synchronization is currently disabled');
      return;
    }

    // Note: We don't check AUTO_SYNC_ON_REFRESH_DISABLED here because this function 
    // is used for manual sync and scheduled sync which should work regardless

    setIsSyncing(true);
    setSyncSuccess(false);
    setSyncError(null);

    try {
      // syncWithFirebase won't throw when offline, it'll just return offline: true
      const { syncedCount, timestamp, offline, fullSync } = await indexedDBService.syncWithFirebase(currentUser.uid, forceSync);

      if (offline) {
        setSyncError('Working in offline mode, using local data');
      } else {
        console.log(`Synced ${syncedCount} items at ${new Date(timestamp).toLocaleString()}`);

        // Set a short-lived success message about real-time syncing
        if (!AUTO_SYNC_ON_REFRESH_DISABLED) {
          setSyncError('Real-time syncing is enabled and working');
          // Clear the message after 3 seconds
          setTimeout(() => setSyncError(null), 3000);
        }
      }

      // Update state
      setPendingChanges(await getPendingChangesCount());
      setLastSyncTime(new Date(timestamp));

      if (!offline) {
        setSyncSuccess(true);
        // Reset success state after 3 seconds
        setTimeout(() => setSyncSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Sync failed:', error);

      if (error instanceof Error) {
        setSyncError(error.message);
      } else {
        setSyncError('Unknown sync error occurred');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  // Context value
  const value: SyncState = {
    isSyncing,
    lastSyncTime,
    pendingChanges,
    syncSuccess,
    syncError,
    isOfflineMode,
    syncWithFirebase
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
};

export default SyncContext;